const DEFAULT_INTERNAL_API_BASE_URL = 'https://staging.services.leadconnectorhq.com/hltestmanager'
const INTERNAL_API_PREFIX = '/hltestmanager'
const ACCESS_TOKEN_KEY = 'access_token'
const REMEMBER_ME_KEY = 'remember_me'
const NEST_VERSION_KEY = 'kaizen'

export const COMMON_API_HEADERS: Record<string, string> = {
  // developer_version: NEST_VERSION_KEY,
}

function trimTrailingSlash(value: string): string {
  return value.replace(/\/+$/, '')
}

function stripTrailingSlash(value: string): string {
  return value.length > 1 ? value.replace(/\/+$/, '') : value
}

function stripInternalPrefix(path: string): string {
  if (path === INTERNAL_API_PREFIX) {
    return ''
  }
  if (path.startsWith(`${INTERNAL_API_PREFIX}/`)) {
    return path.slice(INTERNAL_API_PREFIX.length)
  }
  return path
}

function toAbsoluteUrl(value: string): URL | null {
  try {
    const origin =
      typeof window !== 'undefined' && window.location?.origin
        ? window.location.origin
        : 'http://localhost'
    return new URL(value, origin)
  } catch {
    return null
  }
}

function toHeaderRecord(headers?: HeadersInit): Record<string, string> {
  const normalized: Record<string, string> = {}
  if (!headers) {
    return normalized
  }

  const HeadersConstructor = typeof Headers !== 'undefined' ? Headers : null
  if (HeadersConstructor && headers instanceof HeadersConstructor) {
    headers.forEach((value, key) => {
      normalized[key] = value
    })
    return normalized
  }

  if (Array.isArray(headers)) {
    headers.forEach(([key, value]) => {
      normalized[key] = String(value)
    })
    return normalized
  }

  Object.entries(headers).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      normalized[key] = String(value)
    }
  })

  return normalized
}

function findHeaderKey(headers: Record<string, string>, headerName: string): string | undefined {
  const target = headerName.toLowerCase()
  return Object.keys(headers).find((key) => key.toLowerCase() === target)
}

function hasHeader(headers: Record<string, string>, headerName: string): boolean {
  return Boolean(findHeaderKey(headers, headerName))
}

function deleteHeader(headers: Record<string, string>, headerName: string): void {
  const existingKey = findHeaderKey(headers, headerName)
  if (existingKey) {
    delete headers[existingKey]
  }
}

function getTokenStorage(): Storage | null {
  if (typeof window === 'undefined') {
    return null
  }

  try {
    const rememberMe = window.localStorage.getItem(REMEMBER_ME_KEY)
    return rememberMe === 'false' ? window.sessionStorage : window.localStorage
  } catch {
    return null
  }
}

function getStoredAccessToken(): string | null {
  const storage = getTokenStorage()
  if (!storage) {
    return null
  }

  try {
    return storage.getItem(ACCESS_TOKEN_KEY)
  } catch {
    return null
  }
}

function matchesBaseUrl(requestUrl: URL, baseUrl: string): boolean {
  const parsedBase = toAbsoluteUrl(baseUrl)
  if (!parsedBase || parsedBase.origin !== requestUrl.origin) {
    return false
  }

  const requestPath = stripTrailingSlash(requestUrl.pathname)
  const basePath = stripTrailingSlash(parsedBase.pathname)
  if (basePath === '/') {
    return true
  }
  return requestPath === basePath || requestPath.startsWith(`${basePath}/`)
}

export function getConfiguredApiBaseUrl(): string {
  return (import.meta.env.VITE_API_BASE_URL ?? DEFAULT_INTERNAL_API_BASE_URL).trim()
}

export function resolveInternalApiUrl(endpoint: string, apiBaseUrl: string = getConfiguredApiBaseUrl()): string {
  if (/^https?:\/\//i.test(endpoint)) {
    return endpoint
  }

  const normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`
  const trimmedBase = apiBaseUrl.trim()

  if (!trimmedBase) {
    if (
      normalizedEndpoint === INTERNAL_API_PREFIX ||
      normalizedEndpoint.startsWith(`${INTERNAL_API_PREFIX}/`)
    ) {
      return normalizedEndpoint
    }
    return `${INTERNAL_API_PREFIX}${normalizedEndpoint}`
  }

  const normalizedBase = trimTrailingSlash(trimmedBase)
  if (
    normalizedBase.endsWith(INTERNAL_API_PREFIX) &&
    (normalizedEndpoint === INTERNAL_API_PREFIX ||
      normalizedEndpoint.startsWith(`${INTERNAL_API_PREFIX}/`))
  ) {
    return `${normalizedBase}${stripInternalPrefix(normalizedEndpoint)}`
  }

  return `${normalizedBase}${normalizedEndpoint}`
}

export function isInternalApiUrl(url: string, apiBaseUrl: string = getConfiguredApiBaseUrl()): boolean {
  const trimmedUrl = url.trim()
  if (!trimmedUrl) {
    return false
  }

  const isAbsoluteUrl = /^[a-z][a-z\d+\-.]*:\/\//i.test(trimmedUrl)
  if (
    !isAbsoluteUrl &&
    (trimmedUrl === INTERNAL_API_PREFIX ||
      trimmedUrl.startsWith(`${INTERNAL_API_PREFIX}/`))
  ) {
    return true
  }

  const requestUrl = toAbsoluteUrl(trimmedUrl)
  if (!requestUrl) {
    return false
  }

  const currentOrigin =
    typeof window !== 'undefined' && window.location?.origin
      ? window.location.origin
      : null

  if (
    currentOrigin &&
    requestUrl.origin === currentOrigin &&
    (requestUrl.pathname === INTERNAL_API_PREFIX ||
      requestUrl.pathname.startsWith(`${INTERNAL_API_PREFIX}/`))
  ) {
    return true
  }

  if (apiBaseUrl.trim() && matchesBaseUrl(requestUrl, apiBaseUrl.trim())) {
    return true
  }

  return matchesBaseUrl(requestUrl, DEFAULT_INTERNAL_API_BASE_URL)
}

interface BuildApiHeadersOptions {
  url: string
  includeAuth?: boolean
  isFormData?: boolean
  extraHeaders?: HeadersInit
}

export function buildApiHeaders({
  url,
  includeAuth = true,
  isFormData = false,
  extraHeaders = {},
}: BuildApiHeadersOptions): Record<string, string> {
  const normalizedExtraHeaders = toHeaderRecord(extraHeaders)

  const headers: Record<string, string> = {
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...COMMON_API_HEADERS,
    ...normalizedExtraHeaders,
  }

  if (includeAuth && isInternalApiUrl(url) && !hasHeader(headers, 'Authorization')) {
    const token = getStoredAccessToken()
    if (token) {
      headers.Authorization = `Bearer ${token}`
    }
  }

  if (isFormData && !hasHeader(normalizedExtraHeaders, 'Content-Type')) {
    deleteHeader(headers, 'Content-Type')
  }

  return headers
}
